import { type SVGProps } from '@fortuneo/ui-icons/general/types'
import clsx from 'clsx/lite'
import type { GatsbyLinkProps, Link } from 'gatsby'
import React, {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'

export type ButtonIconPropsBase = {
  className?: string
  Icon: (props: SVGProps) => JSX.Element
  size?: 'small' | 'medium'
}

type InternalLinkProps = {
  component: typeof Link
  to: string
} & Omit<GatsbyLinkProps<any>, 'to'>

type ExternalLinkProps = {
  component: 'a'
  href: string
} & AnchorHTMLAttributes<HTMLAnchorElement>

type ButtonElementProps = {
  component: 'button'
} & ButtonHTMLAttributes<HTMLButtonElement>

export type ButtonIconProps = ButtonIconPropsBase &
  (InternalLinkProps | ExternalLinkProps | ButtonElementProps)

export const ButtonIcon = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonIconProps
>(
  (
    { component: Component, Icon, className, size = 'medium', ...props },
    outerRef
  ) => {
    const innerRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null)

    useImperativeHandle(outerRef, () => innerRef.current, [])

    return (
      <Component
        // @ts-expect-error
        ref={innerRef}
        className={clsx(
          'inline-flex items-center justify-center rounded-full border border-solid border-border-default bg-background-default',
          'hover:bg-theme-default-accent1',
          size === 'small' && 'h-8 w-8',
          size === 'medium' && 'h-12 w-12',
          className
        )}
        {...props}
      >
        <Icon size={size === 'small' ? 16 : 24} />
      </Component>
    )
  }
)
